import { useState, useRef, useEffect } from "react";
import SVG from "react-inlinesvg";
import useOnClickOutside from "../../../hooks/useOnClickOutside";
import { ButtonMenuContainer } from "./styles";
import classNames from "classnames";
import { PROSE_MODE_CATEGORIES } from "..";
import { useSudowriteStore } from "../../../stores/sudowriteStore";
import _ from "lodash";
import { useStoryBibleStore } from "../../../stores/storyBibleStore";
import ModelSelectionModal from "../../ModelSelectionModal";
import { useProjectStore } from "../../../stores/projectStore";
import { useScenesMigrationStore } from "../../../stores/scenesMigrationStore";
import StickyNoteAnchor from "../../StickyTour/StickyNoteAnchor";
import { AnchorIds, TourSignal } from "../../StickyTour/TourTypes";
import { useStickyTourStore } from "../../../stores/stickyTourStore";

const ButtonMenu = (props) => {
  const PROSE_MODES = useStoryBibleStore((state) => state.PROSE_MODES);

  const { proseMode = PROSE_MODES.EXCELLENT, generateProse, selectOnly, onClick, generateLabel, disabled, id, useModelSelectionModal, supportsBenchmarkMode = false } = props;
  const [showSubmenu, setShowSubmenu] = useState(false);
  const submenuControllerRef = useRef();
  useOnClickOutside(submenuControllerRef, () => {
    if (!useModelSelectionModal) {
      setShowSubmenu(false);
    }
    if (project.showModelPicker && showSubmenu) {
      project.update({ showModelPicker: false });
    }
  });

  const subscription = useSudowriteStore((state) => state.subscription);
  const setDraftLastUsedModel = useSudowriteStore((state) => state.setDraftLastUsedModel);
  const project = useProjectStore((state) => state.project);
  const projectId = project?.id;
  const isMigrationOpen = useScenesMigrationStore((state) => state.byProject[project.id]?.modalOpen);
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  useEffect(() => {
    if (project.showModelPicker && useModelSelectionModal && !isMigrationOpen) {
      setShowSubmenu(true);
    }
  }, [project.showModelPicker, useModelSelectionModal, isMigrationOpen]);

  // if (!subscriptionJSON.hasAccessToSpicy) {
  //   // remove the spicy mode if no access
  //   delete proseModes.SPICY;
  // }

  const onSubmenuItemClick = async (mode) => {
    onClick(mode.key);
    setDraftLastUsedModel(projectId, mode.key);
    setShowSubmenu(false);
    if (project.showModelPicker) {
      await project.update({ showModelPicker: false });
    }
  };
  // selectOnly is based on showContinue, which is only true when paused
  // we need a completely different disabled state

  const proseModesGrouped = _.chain(PROSE_MODES)
    .filter((mode) => !mode.benchmarkMode)
    .filter((mode) => {
      return !(mode.category === PROSE_MODE_CATEGORIES.EXPERIMENTAL) || subscription.usesLedgers;
    })
    .filter((mode) => !(mode.requiresMyVoice && !subscription.hasAccessToMyVoice))
    .groupBy((mode) => mode.category)
    .toPairs()
    .sortBy(([category]) => (category === PROSE_MODE_CATEGORIES.CUSTOM ? 0 : 1))
    .fromPairs()
    .value();

  return (
    <ButtonMenuContainer
      ref={submenuControllerRef}
      className={classNames(selectOnly ? "select-only" : "", { disabled: disabled })}
    >
      <StickyNoteAnchor anchorId={AnchorIds.DRAFT_PROSE_GENERATE} />
      <div
        id={id}
        className={classNames("content", { disabled })}
        role="button"
        tabIndex={0}
        onClick={() => {
          sendFlowSignal(TourSignal.DRAFT_PROSE_GENERATE_CLICKED);
          selectOnly ? setShowSubmenu(!showSubmenu) : generateProse();
        }}
      >
        {!selectOnly && (
          <SVG
            className="sparkle"
            src="/assets/AI-sparkle.svg"
          />
        )}
        <div className="labels">
          <div className="action-label">
            <p>{generateLabel}</p>
          </div>
          {(proseMode?.key !== PROSE_MODES.EXCELLENT.key || selectOnly) && <div className="mode-label">{proseMode.label}</div>}
        </div>
      </div>
      <div
        onClick={() => setShowSubmenu(!showSubmenu)}
        className="submenu-control"
        role="button"
        tabIndex={0}
        aria-label="Chapter Generator Options selector, press Enter to Open"
        onKeyPress={(e) => (e.key === "Enter" ? setShowSubmenu(!showSubmenu) : null)}
      >
        <SVG src="/assets/down-chevron.svg" />
      </div>
      {showSubmenu && !useModelSelectionModal && (
        <div className="submenu">
          {Object.entries(proseModesGrouped).map(([groupName, group]) => {
            return (
              <div className={classNames({ group, [groupName]: true })}>
                <div className="group-name">{groupName}</div>
                {Object.values(group).map((mode) => (
                  <div
                    tabIndex={0}
                    className={`item ${proseMode.key === mode.key ? "active" : ""}`}
                    key={mode.key}
                    onClick={() => onSubmenuItemClick(mode)}
                    role="option"
                    aria-selected={proseMode.key === mode.key}
                    onKeyPress={(e) => (e.key === "Enter" ? onSubmenuItemClick(mode) : null)}
                    aria-label={mode.label}
                  >
                    <div className="icon">
                      <SVG src={mode.iconPath} />
                    </div>
                    <div className="labels">
                      <div className="mode-label">
                        {mode.label} {mode.new && <span className="new">NEW</span>} {mode.free && <span className="new">FREE BETA</span>}
                      </div>
                      <div className="sub-label">{mode.subLabel}</div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      <ModelSelectionModal
        isOpen={showSubmenu && useModelSelectionModal}
        onClose={async () => {
          setShowSubmenu(false);
          if (project.showModelPicker) {
            await project.update({ showModelPicker: false });
          }
        }}
        selectedModelId={proseMode.key}
        supportsBenchmarkMode={supportsBenchmarkMode}
        onSelectModelId={(modelId) => {
          onClick(modelId);
          setDraftLastUsedModel(projectId, modelId);
        }}
      />
    </ButtonMenuContainer>
  );
};

export default ButtonMenu;
